import React from "react";
import { motion } from "motion/react";

import InteractiveSidebar from "@/components/Sidebar/InteractiveSidebar";

/**
 * Full-screen mobile navigation: dimmed backdrop plus slide-in {@link InteractiveSidebar}.
 * Intended for use inside `AnimatePresence` so enter/exit motion runs.
 * @param {object} props
 * @param {() => void} props.onBackdropClick - Closes overlay when the scrim is tapped.
 * @param {() => void} props.onSidebarItemClick - Called when a sidebar link is chosen (typically closes menu).
 * @param {Function} props.onCustomerChange - Forwarded to the sidebar customer switcher.
 * @param {Function} [props.onActingUserChange] - Superadmin: forwarded to sidebar (same contract as desktop).
 * @param {Function} [props.onCollapseChange] - Same sidebar prop as desktop; `InteractiveSidebar` only calls it when not mobile.
 * @param {() => void} props.onSignOut - Forwarded to the sidebar sign-out action.
 * @returns {import("react").JSX.Element}
 */
export default function MobileNavOverlay({
  onBackdropClick,
  onSidebarItemClick,
  onCustomerChange,
  onActingUserChange,
  onCollapseChange,
  onSignOut,
}) {
  return (
    <>
      <motion.div
        className="mobile-sidebar-backdrop"
        onClick={onBackdropClick}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          zIndex: 40,
        }}
      />

      <motion.div
        initial={{ x: "-100%" }}
        animate={{ x: 0 }}
        exit={{ x: "-100%" }}
        transition={{ type: "tween", duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
        style={{
          position: "fixed",
          left: 0,
          top: 0,
          zIndex: 50,
          height: "100dvh",
        }}
      >
        <InteractiveSidebar
          isMobile={true}
          onItemClick={onSidebarItemClick}
          onCustomerChange={onCustomerChange}
          onActingUserChange={onActingUserChange}
          onCollapseChange={onCollapseChange}
          handleSignOut={onSignOut}
        />
      </motion.div>
    </>
  );
}
